import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogTitle,
} from '@/components/ui/dialog';
import { formatTime, formatDate, formatScheduleLabel, lessonFormForHeadcount } from '@/lib/utils';
import { hoverDialogBox } from './hoverDialog';
import { compactLessonLabel } from './calendarUtils';

export const RESERVATION_TITLE_PRESETS = [
  'Raumreservierung',
  'Probe',
  'Besprechung',
  'interne Veranstaltung',
];
export const RESERVATION_TITLE_OTHER = 'sonstige Belegung eines Raums';

function initialTitle(name, savedTitles) {
  const title = (name || '').trim();
  const known = new Set([...RESERVATION_TITLE_PRESETS, ...(savedTitles || [])]);
  if (!title || known.has(title)) {
    return { titleChoice: title || RESERVATION_TITLE_PRESETS[0], customTitle: '' };
  }
  return { titleChoice: RESERVATION_TITLE_OTHER, customTitle: title };
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

export default function TerminDialog({
  open, onClose, event, slot, locations, rooms, teachers, savedTitles = [], onSave, onDelete,
  pinned = true, anchor = null,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const privacy = event?.privacy;
  const restricted = privacy === 'busy' || privacy === 'course';

  useEffect(() => {
    if (event) {
      setForm({
        ...(event.kind === 'reservation'
          ? initialTitle(event.name, savedTitles)
          : { titleChoice: '', customTitle: '' }),
        title: event.kind === 'reservation'
          ? (event.name || '')
          : compactLessonLabel(lessonFormForHeadcount(
            formatScheduleLabel(event.name) || event.name || '',
            Array.isArray(event.students) ? event.students.length : 0,
          )),
        date: event.date || '',
        start: event.start || '',
        end: event.end || '',
        location_id: '',
        room_id: event.room_id || '',
        teacher_id: event.teacher_id || '',
        recurrence: event.recurrence || 'once',
        valid_until: event.valid_until || '',
        scope: 'this',
        reservation_id: event.reservation_id || '',
        series_id: event.series_id || '',
        location: event.location_name || '',
        room: event.room_name || '',
        teacher: event.teacher_name || '',
        notes: event.notes || '',
        students: (event.students || []).map((s) => s.name).join('\n'),
      });
    } else if (slot) {
      setForm({
        ...initialTitle('', savedTitles),
        title: '',
        date: slot.date || '',
        start: slot.start || '',
        end: slot.end || '',
        location_id: slot.location_id || '',
        room_id: slot.room_id || '',
        teacher_id: '',
        notes: '',
        recurrence: 'once',
        valid_until: '',
        scope: 'this',
        location: slot.location_name || '',
        room: slot.room_name || '',
        teacher: '',
        students: '',
      });
    }
  }, [event, slot, open]);

  useEffect(() => {
    if (!open) return;
    const roomId = event?.room_id || slot?.room_id;
    if (!roomId) return;
    const locationId = (rooms || []).find((room) => room.id === roomId)?.location_id || '';
    if (!locationId) return;
    setForm((current) => (current.location_id ? current : { ...current, location_id: locationId }));
  }, [open, event, slot, rooms]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openTeaching = (params) => {
    const query = new URLSearchParams(params);
    navigate(`/unterrichtsverwaltung?${query.toString()}`);
  };
  const inp = 'w-full h-8 text-xs border border-slate-200 rounded px-2 bg-white';
  const isReservation = !restricted && (event?.kind === 'reservation' || (!event && !!slot));
  const roomChoices = (rooms || []).filter((room) => !form.location_id || room.location_id === form.location_id);
  const series = isReservation && event?.kind === 'reservation' && event?.recurrence && event.recurrence !== 'once';
  const anchored = Boolean(open && anchor);
  const box = anchored
    ? hoverDialogBox(anchor, { width: window.innerWidth, height: window.innerHeight })
    : null;

  return (
    <Dialog open={open} modal={!anchored} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={`max-w-lg p-0 gap-0 overflow-hidden${box ? ' translate-x-0 translate-y-0' : ''}${anchored && pinned === false ? ' pointer-events-none' : ''}`}
        style={box ? { left: box.left, top: box.top, width: box.width, maxWidth: box.width, transform: 'none' } : undefined}
        hideCloseButton
        hideOverlay={anchored}
        data-testid="calendar-termin-dialog"
        onInteractOutside={(event) => { if (anchored) event.preventDefault(); }}
        onPointerDownOutside={(event) => { if (anchored) event.preventDefault(); }}
        onFocusOutside={(event) => { if (anchored) event.preventDefault(); }}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
          <button type="button" className="h-8 px-3 rounded bg-rose-600 text-white text-xs font-semibold"
                  onClick={onClose} data-testid="calendar-termin-cancel">Abbrechen</button>
          <DialogTitle className="text-sm font-bold tracking-wide">{isReservation ? 'RESERVIERUNG' : 'TERMIN'}</DialogTitle>
          <div className="flex gap-1.5">
          {restricted ? (
            <button type="button" className="h-8 px-3 rounded bg-slate-700 text-white text-xs font-semibold"
                    onClick={onClose} data-testid="calendar-termin-save">
              Schließen
            </button>
          ) : (
            <button type="button" className="h-8 px-3 rounded bg-emerald-600 text-white text-xs font-semibold"
                    data-testid="calendar-termin-save"
                    onClick={() => {
                      if (isReservation) {
                        const title = form.titleChoice === RESERVATION_TITLE_OTHER
                          ? (form.customTitle || '').trim()
                          : (form.titleChoice || '');
                        onSave?.({ ...form, title });
                      } else if (event?.course_id) navigate(`/courses/${event.course_id}`);
                      else onClose();
                    }}>
              Speichern
            </button>
          )}
          </div>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {isReservation ? (
            <>
              <Field label="Bezeichnung">
                <select className={inp} value={form.titleChoice || RESERVATION_TITLE_PRESETS[0]}
                        aria-label="Bezeichnung" data-testid="reservation-title"
                        onChange={(e) => set('titleChoice', e.target.value)}>
                  {RESERVATION_TITLE_PRESETS.map((title) => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                  {(savedTitles || []).filter((title) => title && !RESERVATION_TITLE_PRESETS.includes(title)).map((title) => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                  <option value={RESERVATION_TITLE_OTHER}>{RESERVATION_TITLE_OTHER}</option>
                </select>
              </Field>
              {form.titleChoice === RESERVATION_TITLE_OTHER && (
                <Field label="Eigene Bezeichnung">
                  <input className={inp} value={form.customTitle || ''} aria-label="Eigene Bezeichnung"
                         data-testid="reservation-custom-title"
                         onChange={(e) => set('customTitle', e.target.value)} />
                </Field>
              )}
              <Field label="Datum">
                <input type="date" className={inp} value={form.date || ''} aria-label="Datum"
                       data-testid="reservation-date"
                       onChange={(e) => set('date', e.target.value)} />
              </Field>
              <Field label="Beginn">
                <input type="time" className={inp} value={(form.start || '').slice(0, 5)} aria-label="Beginn"
                       data-testid="reservation-start"
                       onChange={(e) => set('start', e.target.value)} />
              </Field>
              <Field label="Ende">
                <input type="time" className={inp} value={(form.end || '').slice(0, 5)} aria-label="Ende"
                       data-testid="reservation-end"
                       onChange={(e) => set('end', e.target.value)} />
              </Field>
              <Field label="Wiederholung">
                <select className={inp} value={form.recurrence || 'once'} aria-label="Wiederholung"
                        data-testid="reservation-recurrence"
                        onChange={(e) => set('recurrence', e.target.value)}>
                  <option value="once">Einmalig</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="biweekly">14-tägig</option>
                  <option value="monthly">Monatlich</option>
                </select>
              </Field>
              {form.recurrence && form.recurrence !== 'once' && (
                <Field label="Gültig bis">
                  <input type="date" className={inp} value={form.valid_until || ''} aria-label="Gültig bis"
                         data-testid="reservation-until"
                         onChange={(e) => set('valid_until', e.target.value)} />
                </Field>
              )}
              <Field label="Veranstaltungsort">
                <select className={inp} value={form.location_id || ''} aria-label="Veranstaltungsort"
                        data-testid="reservation-location"
                        onChange={(e) => setForm((current) => ({ ...current, location_id: e.target.value, room_id: '' }))}>
                  <option value="">—</option>
                  {(locations || []).map((location) => (
                    <option key={location.id} value={location.id}>{location.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Raum">
                <select className={inp} value={form.room_id || ''} aria-label="Raum"
                        data-testid="reservation-room"
                        onChange={(e) => set('room_id', e.target.value)}>
                  <option value="">—</option>
                  {roomChoices.map((room) => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Person">
                <select className={inp} value={form.teacher_id || ''} aria-label="Person"
                        data-testid="reservation-person"
                        onChange={(e) => set('teacher_id', e.target.value)}>
                  <option value="">ohne Person</option>
                  {(teachers || []).map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.last_name}, {teacher.first_name}
                    </option>
                  ))}
                </select>
              </Field>
              {series && (
                <Field label="Umfang">
                  <select className={inp} value={form.scope || 'this'} aria-label="Umfang"
                          data-testid="reservation-scope"
                          onChange={(e) => set('scope', e.target.value)}>
                    <option value="this">Nur diesen Termin</option>
                    <option value="following">Diesen Termin und alle folgenden</option>
                    <option value="series">Die ganze Serie</option>
                  </select>
                </Field>
              )}
              <Field label="Bemerkung">
                <textarea className="w-full min-h-[72px] text-xs border border-slate-200 rounded px-2 py-1"
                          aria-label="Bemerkung" value={form.notes || ''}
                          onChange={(e) => set('notes', e.target.value)} />
              </Field>
              {event?.reservation_id && (
                <button type="button" className="text-xs text-rose-700 underline"
                        data-testid="reservation-delete"
                        onClick={() => onDelete?.(form)}>
                  Reservierung löschen
                </button>
              )}
            </>
          ) : (
          <>
          <Field label="Bezeichnung">
            <input className={inp} value={form.title || ''} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <Field label="Datum">
            <input className={inp} value={form.date ? formatDate(form.date) : ''} readOnly />
          </Field>
          <Field label="Uhrzeit">
            <div className="flex gap-1">
              <input className={inp} value={formatTime(form.start) || ''} readOnly />
              <input className={inp} value={formatTime(form.end) || ''} readOnly />
            </div>
          </Field>
          {privacy !== 'busy' && (
            <Field label="Teilnehmer:in">
              <div className="min-h-8 rounded border border-slate-200 bg-white px-2 py-1">
                {(event?.students || []).length ? event.students.map((student) => (
                  <button
                    key={student.enrollment_id || student.id || student.name}
                    type="button"
                    className="block text-left text-xs text-brand-red hover:underline"
                    onClick={() => openTeaching(student.enrollment_id
                      ? { enrollment_id: student.enrollment_id }
                      : { course_id: event.course_id, student_id: student.id })}
                  >
                    {student.name}
                  </button>
                )) : <span className="text-xs text-slate-400">—</span>}
              </div>
            </Field>
          )}
          {privacy !== 'busy' && (
            <Field label="Lehrkraft">
              {event?.course_id && form.teacher ? (
                <button
                  type="button"
                  className={`${inp} text-left text-brand-red hover:underline`}
                  onClick={() => openTeaching({ course_id: event.course_id })}
                >
                  {form.teacher}
                </button>
              ) : (
                <input className={inp} value={form.teacher || ''} readOnly />
              )}
            </Field>
          )}
          <Field label="Veranstaltungsort">
            <input className={inp} value={form.location || ''} readOnly />
          </Field>
          <Field label="Raum">
            <input className={inp} value={form.room || ''} readOnly />
          </Field>
          <Field label="Bemerkung">
            <textarea className="w-full min-h-[72px] text-xs border border-slate-200 rounded px-2 py-1"
                      value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
          </Field>
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
